<div class="page-header">
  <div class="title-section">
    <h2 class="page-title">{{ isEditMode ? 'Modifier le workflow' : 'Créer un workflow' }}</h2>
    <p class="page-subtitle">
      Définissez les étapes, les transitions de statuts et les équipes responsables.
    </p>
  </div>
  <a routerLink="/settings/workflows" class="btn btn-outline btn-with-icon back-link">
    <mat-icon aria-hidden="true">arrow_back</mat-icon>
    Retour à la liste
  </a>
</div>

@if (errorMessage || validationErrors.length > 0) {
@if (isSequenceError()) {
<div class="validation-banner mb-4" role="alert">
  <div class="validation-banner-head">
    <mat-icon aria-hidden="true">error</mat-icon>
    <span>Incohérence dans l'ordre des statuts</span>
  </div>
  @if (validationErrors.length > 0) {
  <ul class="validation-errors-list">
    @for (error of validationErrors; track error) {
    <li>{{ error }}</li>
    }
  </ul>
  } @else {
  <p class="validation-banner-text">{{ errorMessage }}</p>
  }
  <p class="validation-banner-hint">
    Vérifiez que le statut final de l'étape N est identique au statut initial de l'étape N+1.
  </p>
</div>
} @else {
<div class="alert alert-error mb-4">
  @if (validationErrors.length > 1) {
  <ul class="error-list m-0">
    @for (error of validationErrors; track error) {
    <li>{{ error }}</li>
    }
  </ul>
  } @else {
  {{ errorMessage || validationErrors[0] }}
  }
</div>
}
}
@if (successMessage) {
<div class="alert alert-success mb-4">{{ successMessage }}</div>
}

@if (isLoading) {
<div class="card shadow-sm loading-card">
  <p>Chargement du formulaire...</p>
</div>
} @else {
<div class="card shadow-sm">
  <form [formGroup]="form" (ngSubmit)="onSave()" class="workflow-form">
    <div class="form-row two-col">
      <div class="form-group">
        <label>NOM</label>
        <input class="form-control" formControlName="name" type="text" placeholder="Ex: Workflow standard" />
      </div>

      <div class="form-group">
        <label>STATUT DU WORKFLOW</label>
        <mat-form-field appearance="outline" class="w-full custom-select">
          <mat-select formControlName="status">
            @for (status of statusesEnum; track status) {
            <mat-option [value]="status">{{ status }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label>TYPE DE WORKFLOW</label>
        <mat-form-field appearance="outline" class="w-full custom-select">
          <mat-select formControlName="type" (selectionChange)="onWorkflowTypeChanged()">
            @for (type of workflowTypes; track type) {
            <mat-option [value]="type">{{ type }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <div class="form-group">
      <label>DESCRIPTION</label>
      <textarea class="form-control" formControlName="description" rows="2"></textarea>
    </div>

    <div class="steps-head">
      <div>
        <h3>Étapes du workflow</h3>
        <p class="steps-subtitle">Glissez-déposez les cartes pour réordonner les étapes.</p>
      </div>
      <button type="button" class="btn btn-outline btn-sm" (click)="addStep()">
        <mat-icon aria-hidden="true">add</mat-icon>
        Ajouter une étape
      </button>
    </div>

    <div formArrayName="steps" class="steps-list" cdkDropList (cdkDropListDropped)="onStepDropped($event)">
      @for (stepCtrl of steps.controls; track $index; let stepIndex = $index) {
      <div class="step-card" [formGroupName]="stepIndex" cdkDrag [class.step-has-error]="stepErrors[stepIndex]">
        @if (stepErrors[stepIndex]) {
        <div class="step-error-message">
          <mat-icon aria-hidden="true">error_outline</mat-icon>
          <span>{{ stepErrors[stepIndex] }}</span>
        </div>
        }
        <div class="step-card-header">
          <div class="step-title-group">
            <button type="button" class="drag-handle" cdkDragHandle title="Réorganiser l'étape">
              <mat-icon aria-hidden="true">drag_indicator</mat-icon>
            </button>
            <h4>Étape {{ stepIndex + 1 }}</h4>
            <span class="step-order-badge">Ordre {{ stepIndex + 1 }}</span>
          </div>

          <button type="button" class="btn-icon delete" (click)="removeStep(stepIndex)" title="Retirer l'étape">
            <mat-icon aria-hidden="true">delete</mat-icon>
          </button>
        </div>

        <div class="form-row two-col">
          <div class="form-group">
            <label>NOM DE L'ÉTAPE</label>
            <input class="form-control" formControlName="name" type="text" placeholder="Ex: Validation étude" />
          </div>
          <div class="form-group">
            <label>DESCRIPTION DE L'ÉTAPE</label>
            <input class="form-control" formControlName="description" type="text" placeholder="Optionnel" />
          </div>
        </div>

        <div class="form-row two-col">
          <div class="form-group">
            <label>STATUT INITIAL</label>
            <mat-form-field appearance="outline" class="w-full custom-select">
              <mat-select formControlName="initialStatusId">
                @for (status of statuses; track status.id) {
                <mat-option [value]="status.id"
                  [disabled]="status.archived && status.id !== stepCtrl.get('initialStatusId')?.value">
                  {{ status.name }} @if (status.archived) { (Archivé) }
                </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          <div class="form-group">
            <label>STATUT FINAL</label>
            <mat-form-field appearance="outline" class="w-full custom-select">
              <mat-select formControlName="finalStatusId">
                @for (status of statuses; track status.id) {
                <mat-option [value]="status.id"
                  [disabled]="status.archived && status.id !== stepCtrl.get('finalStatusId')?.value">
                  {{ status.name }} @if (status.archived) { (Archivé) }
                </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        </div>

        <div class="assign-head">
          <h5>Équipes assignées</h5>
          <button type="button" class="btn btn-outline btn-sm" (click)="addAssignment(stepIndex)">
            <mat-icon aria-hidden="true">add</mat-icon>
            Ajouter équipe
          </button>
        </div>

        <div class="assign-list" formArrayName="teamAssignments">
          @for (assignmentCtrl of getAssignments(stepIndex).controls; track $index; let assignmentIndex = $index) {
          <div class="assign-row" [formGroupName]="assignmentIndex">
            <mat-form-field appearance="outline" class="w-full custom-select team-select">
              <mat-select formControlName="teamId" placeholder="Équipe">
                @for (team of teams; track team.id) {
                <mat-option [value]="team.id"
                  [disabled]="team.archived && team.id !== assignmentCtrl.get('teamId')?.value">
                  {{ team.name }} @if (team.archived) { (Archivée) }
                </mat-option>
                }
              </mat-select>
            </mat-form-field>

            <div class="permissions-grid">
              @for (permission of permissions; track permission) {
              <label class="perm-item">
                <mat-checkbox [checked]="hasPermission(stepIndex, assignmentIndex, permission)"
                  (change)="togglePermission(stepIndex, assignmentIndex, permission, $event.checked)">
                  {{ permission }}
                </mat-checkbox>
              </label>
              }
            </div>

            <button type="button" class="btn-icon delete" (click)="removeAssignment(stepIndex, assignmentIndex)"
              title="Retirer l'affectation">
              <mat-icon aria-hidden="true">remove_circle</mat-icon>
            </button>
          </div>
          } @empty {
          <p class="empty-text">Aucune équipe assignée</p>
          }
        </div>
      </div>
      }
    </div>

    <div class="form-actions">
      @if (isEditMode) {
      <button type="button" class="btn btn-danger-outline" (click)="onRequestDelete()">Supprimer / Archiver</button>
      }
      <button type="button" class="btn btn-outline" (click)="onCancel()">Annuler</button>
      <button type="submit" class="btn btn-primary" [disabled]="isSaving">
        {{ isSaving ? 'Enregistrement...' : 'Enregistrer le workflow' }}
      </button>
    </div>
  </form>
</div>clear
}

@if (isDeleteConfirmOpen) {
<app-confirmation-dialog [dialogTitle]="getDeleteDialogTitle()" [message]="getDeleteDialogMessage()"
  [confirmLabel]="getDeleteDialogLabel()" [isDanger]="true" (confirm)="confirmDelete()" (cancel)="cancelDelete()">
</app-confirmation-dialog>
}
